import { createContext, useContext, useMemo, type ReactNode } from 'react';

/**
 * WHICH NODE the auto-form is currently editing — a stable address for one section or one block, so that
 * work started in a panel can outlive that panel.
 *
 * The controls only ever knew their `field.id` ("image"), which is the same string on every card of every
 * section. A background upload needs to say WHICH card's image it is filling, and it must still be able to
 * say it after the inspector has moved on. The section/block inspectors already hold those ids, so they
 * publish them here rather than threading a new prop through the whole `FieldControl` switch.
 *
 * `reveal` brings the seller back to that node — the topbar uses it when an upload fails with no panel open.
 */
export interface FieldTarget {
  /** `section:<id>` or `block:<sectionId>:<blockId>`. */
  key: string;
  reveal?: () => void;
}

const FieldTargetContext = createContext<FieldTarget>({ key: 'unscoped' });

export function FieldTargetProvider({
  target,
  reveal,
  children,
}: {
  target: string;
  reveal?: () => void;
  children: ReactNode;
}) {
  const value = useMemo(() => ({ key: target, reveal }), [target, reveal]);
  return <FieldTargetContext.Provider value={value}>{children}</FieldTargetContext.Provider>;
}

/** The registry key for one field of the node being edited, plus the way back to it. */
export function useFieldTarget(fieldId: string): FieldTarget {
  const target = useContext(FieldTargetContext);
  return useMemo(
    () => ({ key: `${target.key}#${fieldId}`, reveal: target.reveal }),
    [target, fieldId],
  );
}
